import { Box, Typography, Grid, Tooltip } from "@mui/material";
import "../../styles/common.css";
import { useState } from "react";
import FeedBackIcon from "../../assets/common/feedBackIcon.svg";
import FeedbackPopup from "../Common/FeedbackPopUp";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import useGlobalContext from "../../context/useGlobalContext";

interface CriticalTopic {
  topic: string;
  why_must_cover: string;
  what_to_achieve: string;
}

interface StrategicTopic {
  topic: string;
  objective: string;
  why_it_moves_partnership_forward: string;
}

interface MeetingFlowItem {
  topic: string;
  description: string;
}

interface PrepNotesData {
  value: {
    executive_summary?: string;
    meeting_agenda_recommendations?: {
      critical_topics?: CriticalTopic[];
      strategic_topics?: StrategicTopic[];
      value_reinforcement_topics?: {
        reinforce?: string;
        highlights?: string;
        business_impact?: string;
      };
      meeting_flow_recommendation?: MeetingFlowItem[];
    };
  };
  ai_analysis_status?: string;
}

interface Props {
  data?: PrepNotesData | null;
  interactionId: string
}

const PrepNotesSlideOver: React.FC<Props> = ({ data, interactionId }) => {

  const value = data?.value ?? {};
  const rec = value.meeting_agenda_recommendations ?? {};

  const criticalTopics = Array.isArray(rec.critical_topics)
    ? rec.critical_topics
    : [];

  const strategicTopics = Array.isArray(rec.strategic_topics)
    ? rec.strategic_topics
    : [];

  const meetingFlow = Array.isArray(rec.meeting_flow_recommendation)
    ? rec.meeting_flow_recommendation
    : [];

  const valueReinforcement = rec.value_reinforcement_topics ?? {};
  const [tooltipOpen, setTooltipOpen] = useState<string | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackReference, setFeedbackReference] = useState("");
  const { showSnackbar } = useDisplaySnackbar();
  const { featureMap } = useGlobalContext();
  const userFeedback = featureMap?.idp_user_feedback === true;

  const handleFeedbackClick = (section: string) => {
    setTooltipOpen(null);
    setFeedbackReference(section);
    setFeedbackOpen(true);
  };

  return (
    <Box className="prep-notes-slide-over-container">
      {/* EXECUTIVE SUMMARY CARD */}
      <Box className="prep-notes-summary-card">
        <Typography className="p2p-slides-summary">
          <span className="head">Insights: </span>
          <span className="body">
            {value.executive_summary || "No executive summary available"}
          </span>
        </Typography>
      </Box>

      {/* GRID */}
      <Grid container spacing={2}>
        {/* Critical Topics */}
        <Grid item xs={12} md={6}>
          <Box className="prep-notes-topic-card">
            <Typography className="p2p-slides-summary prep-topic-header-mb">
              <span className="head">Critical Topics </span>
              <span className="head prep-topic-subtitle">
                (Must Cover):
              </span>
              {userFeedback &&
              <Tooltip
                title="Give feedback on this tag"
                placement="top"
                arrow
                classes={{ tooltip: "custom-tooltip", arrow: "custom-tooltip-arrow" }}
                open={tooltipOpen === "critical" && !feedbackOpen}
                onOpen={() => setTooltipOpen("critical")}
                onClose={() => setTooltipOpen(null)}
              >
                <img
                  src={FeedBackIcon}
                  alt="feedback"
                  className="feedback-icon"
                  onClick={() => handleFeedbackClick("Critical Topics")}
                />
              </Tooltip>
              }
            </Typography>

            {criticalTopics.length === 0 && (
              <Typography className="empty-state">No data available</Typography>
            )}

            {criticalTopics.map((t, idx) => (
              <Box key={idx} className="prep-topic-section-box">
                <Typography className="prep-topic-number">
                  {idx + 1}. {t?.topic || "No topic"}
                </Typography>

                <ul className="prep-topic-ul">
                  <li>
                    <Typography className="prep-topic-bullet">
                      • Why: <span>{t?.why_must_cover || "N/A"}</span>
                    </Typography>
                  </li>

                  <li>
                    <Typography className="prep-topic-bullet">
                      • What to achieve:{" "}
                      <span>{t?.what_to_achieve || "N/A"}</span>
                    </Typography>
                  </li>
                </ul>
              </Box>
            ))}
            <Box className="prep-notes-fade-effect" />
          </Box>
        </Grid>

        {/* Strategic Topics */}
        <Grid item xs={12} md={6}>
          <Box className="prep-notes-topic-card">
            <Typography className="p2p-slides-summary prep-topic-header-mb">
              <span className="head">Strategic Topics </span>
              <span className="head prep-topic-subtitle">
                (Move Partnership Forward):
              </span>
              {userFeedback &&
              <Tooltip
                  title="Give feedback on this tag"
                  placement="top"
                  arrow
                  classes={{ tooltip: "custom-tooltip", arrow: "custom-tooltip-arrow" }}
                  open={tooltipOpen === "strategic" && !feedbackOpen}
                  onOpen={() => setTooltipOpen("strategic")}
                  onClose={() => setTooltipOpen(null)}
                >
                  <img
                    src={FeedBackIcon}
                    alt="feedback"
                    className="feedback-icon"
                    onClick={() => handleFeedbackClick("Stratigic Topics")}
                  />
                </Tooltip>
              }
            </Typography>

            {strategicTopics.length === 0 && (
              <Typography className="empty-state">No data available</Typography>
            )}

            {strategicTopics.map((t, idx) => (
              <Box key={idx} className="prep-topic-section-box">
                <Typography className="prep-topic-number">
                  {idx + 1}. {t?.topic || "No topic"}
                </Typography>

                <ul className="prep-topic-ul">
                  <li>
                    <Typography className="prep-topic-bullet">
                      • Objective: <span>{t?.objective || "N/A"}</span>
                    </Typography>
                  </li>

                  <li>
                    <Typography className="prep-topic-bullet">
                      • Why:{" "}
                      <span>{t?.why_it_moves_partnership_forward || "N/A"}</span>
                    </Typography>
                  </li>
                </ul>
              </Box>
            ))}
            <Box className="prep-notes-fade-effect" />
          </Box>
        </Grid>

        {/* Value Reinforcement */}
        <Grid item xs={12} md={6}>
          <Box className="prep-notes-topic-card">
            <Typography className="p2p-slides-summary prep-topic-header-mb">
              <span className="head">Value Reinforcement Topics:</span>
              {userFeedback && 
              <Tooltip
                  title="Give feedback on this tag"
                  placement="top"
                  arrow
                  classes={{ tooltip: "custom-tooltip", arrow: "custom-tooltip-arrow" }}
                  open={tooltipOpen === "value" && !feedbackOpen}
                  onOpen={() => setTooltipOpen("value")}
                  onClose={() => setTooltipOpen(null)}
                >
                  <img
                    src={FeedBackIcon}
                    alt="feedback"
                    className="feedback-icon"
                    onClick={() => handleFeedbackClick("Value Reinforcement Topics")}
                  />
                </Tooltip>
              }
            </Typography>

            {!valueReinforcement || (!valueReinforcement.highlights && !valueReinforcement.business_impact && !valueReinforcement.reinforce) ? (
              <Typography className="empty-state">No data available</Typography>
            ) : (
                <Box className="prep-topic-section-box prep-topic-section-box-no-margin">
                  <ul className="prep-topic-ul-small">
                    <li>
                      <Typography className="prep-topic-bullet">
                        <span className="prep-topic-title">• Highlight:</span>{" "}
                        <span>{valueReinforcement.highlights || "N/A"}</span>
                      </Typography>
                    </li>
                    <li>
                      <Typography className="prep-topic-bullet">
                        <span className="prep-topic-title">
                          • Business Impact:
                        </span>{" "}
                        <span>{valueReinforcement.business_impact || "N/A"}</span>
                      </Typography>
                    </li>
                    <li>
                      <Typography className="prep-topic-bullet">
                        <span className="prep-topic-title">• Reinforce:</span>{" "}
                        <span>{valueReinforcement.reinforce || "N/A"}</span>
                      </Typography>
                    </li>
                  </ul>
                </Box>
            )}
            <Box className="prep-notes-fade-effect" />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box className="prep-notes-topic-card">
            <Typography className="p2p-slides-summary prep-topic-header-mb">
              <span className="head">Meeting Flow Recommendation:</span>
            </Typography>

            {meetingFlow.length === 0 && (
              <Typography className="empty-state">No data available</Typography>
            )}

            {meetingFlow.map((step, idx) => (
              <Box key={idx} className="prep-topic-flow-box">
                <Typography className="prep-topic-bullet">
                  <span className="prep-topic-title">• {step?.topic}:</span>{" "}
                  <span>{step?.description || "N/A"}</span>
                </Typography>
              </Box>
            ))}
            <Box className="prep-notes-fade-effect" />
          </Box>
        </Grid>
      </Grid>
      <FeedbackPopup
        open={feedbackOpen}
        interactionId={interactionId || ""}
        feedbackKey="prep_notes"
        feedbackReference={feedbackReference}
        onClose={() => setFeedbackOpen(false)}
        onSubmit={() => {
          showSnackbar("success", "Feedback submitted successfully.");
        }}
      />
    </Box>
  );
};

export default PrepNotesSlideOver;
